import { useEffect, useRef } from "react";
import { getItem } from "@/game/rooms";

export const DialogBox = ({ line, more, onClose, onChoose }) => (
  <div className="absolute left-2 right-2 bottom-2 sm:left-6 sm:right-6 sm:bottom-6 z-20 panel px-3 py-2 sm:px-5 sm:py-4 rise" data-testid="dialog-box" onClick={onClose}>
    {line.who && <p className={`pixel-text text-base sm:text-xl tracking-widest ${line.who === "Joe" ? "text-[#7DD3FC]" : "text-[#F59E0B]"}`} data-testid="dialog-speaker">{line.who.toUpperCase()}</p>}
    <p className="pixel-text text-base sm:text-2xl text-[#F3F4F6] leading-tight sm:leading-snug" data-testid="dialog-text">{line.text}</p>
    {line.choices ? (
      <div className="flex flex-col sm:flex-row gap-3 mt-3" onClick={(e) => e.stopPropagation()}>
        {line.choices.map((c, i) => (
          <button key={c.note} className={`pixel-btn ${i ? "ghost" : ""}`} onClick={() => onChoose(c)} data-testid={`dialog-choice-${i}`}>[{i + 1}] {c.label}</button>
        ))}
      </div>
    ) : <p className="pixel-text text-base text-[#64748B] text-right mt-1 blink">{more ? "[E] next ▸" : "[E] continue"}</p>}
  </div>
);

export const Prompt = ({ label }) => (
  <div className="absolute top-14 left-1/2 -translate-x-1/2 z-10 pixel-text text-lg sm:text-xl bg-black/70 border border-[#374151] px-3 py-0.5 text-[#F59E0B]" data-testid="interaction-prompt">
    [E] {label}
  </div>
);

export const Inventory = ({ items, onRead }) => (
  <div>
  <div className="flex gap-1.5 flex-wrap" data-testid="inventory-bar">
    {Array.from({ length: Math.max(6, items.length) }).map((_, i) => {
      const it = items[i] && getItem(items[i]);
      return (
        <button key={i} title={it ? `${it.name} (click to read)` : "Empty"} disabled={!it} onClick={() => it && onRead(it.id)}
          className="w-9 h-9 bg-[#1A1D27] border-2 border-[#374151] flex items-center justify-center enabled:hover:border-[#DC2626] transition-colors" data-testid={`inventory-slot-${i}`}>
          {it && <span className="block w-4 h-3" style={{ background: it.color, boxShadow: "0 0 0 1px #000" }} />}
        </button>
      );
    })}
  </div>
  {items.length > 0 && <p className="pixel-text text-base text-[#9CA3AF] mt-1" data-testid="inventory-names">{items.map((i) => getItem(i)?.name).filter(Boolean).join(" · ")}</p>}
  </div>
);

const HoldButton = ({ k, input, label, testid }) => {
  const ref = useRef(null);
  const set = (v) => { if (k === "interact") { if (v) input.current.tap = true; } else input.current[k] = v; ref.current?.classList.toggle("on", v); };
  return (
    <button ref={ref} className="ctrl-btn" data-testid={testid}
      onPointerDown={(e) => { e.preventDefault(); set(true); }} onPointerUp={() => set(false)}
      onPointerLeave={() => set(false)} onPointerCancel={() => set(false)}>{label}</button>
  );
};

export const MobileControls = ({ input }) => (
  <div className="flex items-center justify-between w-full" data-testid="mobile-controls">
    <div className="flex gap-3">
      <HoldButton k="left" input={input} label="◀" testid="ctrl-left-button" />
      <HoldButton k="right" input={input} label="▶" testid="ctrl-right-button" />
    </div>
    <HoldButton k="interact" input={input} label="E" testid="canvas-interact-btn" />
  </div>
);

const KEYS = { ArrowLeft: "left", a: "left", A: "left", ArrowRight: "right", d: "right", D: "right", e: "interact", E: "interact", " ": "interact" };

export const useKeyboard = (input) => {
  useEffect(() => {
    const on = (v) => (ev) => {
      const k = KEYS[ev.key];
      if (!k || ev.target.tagName === "INPUT") return;
      ev.preventDefault();
      if (k === "interact") { if (v && !ev.repeat) input.current.tap = true; } else input.current[k] = v;
    };
    const down = on(true), up = on(false);
    window.addEventListener("keydown", down); window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [input]);
};
